import React, { useState, useEffect } from 'react';
import styled, { keyframes } from 'styled-components';
import { FiSearch, FiStar, FiUsers, FiHardDrive, FiScissors, FiMonitor, FiChevronDown } from 'react-icons/fi';
import { getSearchOptions } from '../../services/api';

// Animation keyframes
const fadeInUp = keyframes`
  from {
    opacity: 0;
    transform: translateY(30px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

const scaleIn = keyframes`
  from {
    opacity: 0;
    transform: scale(0.8);
  }
  to {
    opacity: 1;
    transform: scale(1);
  }
`;

// Removed border-radius morphing to preserve custom corner shapes

const HeroContainer = styled.section`
  width: 100%;
  min-height: 60vh;
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;
  overflow: visible;
  color: white;
  padding: 1rem 0;
  z-index: 10;

  background: ${props => props.theme.colors.primaryDark};

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background-image: 
      repeating-linear-gradient(
        45deg,
        transparent,
        transparent 3px,
        rgba(255, 255, 255, 0.03) 3px,
        rgba(255, 255, 255, 0.03) 6px
      ),
      repeating-linear-gradient(
        -45deg,
        transparent,
        transparent 2px,
        rgba(255, 255, 255, 0.02) 2px,
        rgba(255, 255, 255, 0.02) 4px
      );
    pointer-events: none;
  }

  &::after {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background-image: 
      radial-gradient(circle at 10% 90%, rgba(255, 255, 255, 0.05) 0%, transparent 30%),
      radial-gradient(circle at 90% 10%, rgba(255, 255, 255, 0.04) 0%, transparent 30%);
    pointer-events: none;
  }

  /* Removed old pattern overlays to show clean background image */

  /* iPad Portrait */
  @media (min-width: 768px) and (max-width: 1024px) and (orientation: portrait) {
    padding: 2.5rem 0;
    min-height: 55vh;
    background-position: center 30%;
  }

  /* iPad Landscape */
  @media (min-width: 768px) and (max-width: 1024px) and (orientation: landscape) {
    padding: 2rem 0;
    min-height: 50vh;
    background-position: center 25%;
  }

  /* Small laptops and 125% zoom */
  @media (min-width: 1024px) and (max-width: 1366px) {
    padding: 2rem 0;
    min-height: 55vh;
  }

  /* 13-15 inch laptops */
  @media (min-width: 1366px) and (max-width: 1600px) {
    padding: 2.5rem 0;
    min-height: 60vh;
  }

  @media (max-width: 768px) {
    padding: 2rem 0;
    min-height: 50vh;
    background-position: center 20%;
  }

  /* Extra small screens */
  @media (max-width: 480px) {
    padding: 1.5rem 0;
    min-height: 45vh;
    overflow: hidden;
  }

  /* Very small screens */
  @media (max-width: 360px) {
    padding: 1rem 0;
    min-height: 40vh;
    overflow: hidden;
  }
`;

const HeroContent = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 1rem;
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 3rem;
  align-items: center;
  position: relative;
  z-index: 1;
  overflow: visible;

  /* iPad Portrait */
  @media (min-width: 768px) and (max-width: 1024px) and (orientation: portrait) {
    grid-template-columns: 1fr 1fr;
    gap: 2rem;
    max-width: 900px;
  }

  /* iPad Landscape */
  @media (min-width: 768px) and (max-width: 1024px) and (orientation: landscape) {
    grid-template-columns: 1fr 1fr;
    gap: 1.5rem;
    max-width: 1000px;
  }

  /* Small laptops and 125% zoom */
  @media (min-width: 1024px) and (max-width: 1366px) {
    grid-template-columns: 1fr 1fr;
    gap: 2.5rem;
    max-width: 1100px;
  }

  /* 150% zoom and medium laptops */
  @media (min-width: 1366px) and (max-width: 1600px) {
    grid-template-columns: 1fr 1fr;
    gap: 2.75rem;
    max-width: 1150px;
  }

  /* 175% zoom and large screens */
  @media (min-width: 1600px) and (max-width: 1920px) {
    grid-template-columns: 1fr 1fr;
    gap: 3rem;
    max-width: 1200px;
  }

  /* 13-15 inch laptops */
  @media (min-width: 1366px) and (max-width: 1600px) {
    grid-template-columns: 1fr 1fr;
    gap: 3rem;
    max-width: 1200px;
  }

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 0;
    text-align: center;
    max-width: 600px;
  }

  /* Extra small screens */
  @media (max-width: 480px) {
    padding: 0 1rem;
    max-width: 100%;
    gap: 1rem;
  }

  /* Very small screens */
  @media (max-width: 360px) {
    padding: 0 0.75rem;
    max-width: 100%;
    gap: 0.75rem;
  }
`;

const CertifiedBadge = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-family: 'Shadows Into Light', cursive;
  font-size: 1.8rem;
  font-weight: 600;
  color: ${props => props.theme.colors.secondaryLight};
  margin-bottom: 1.5rem;
  opacity: 0;
  animation: ${fadeInUp} 0.8s ease-out 0.2s forwards;

  svg {
    color: ${props => props.theme.colors.secondaryLight};
    font-size: 2rem;
  }

  /* iPad Portrait */
  @media (min-width: 768px) and (max-width: 1024px) and (orientation: portrait) {
    font-size: 1.6rem;
    
    svg {
      font-size: 1.8rem;
    }
  }

  /* iPad Landscape */
  @media (min-width: 768px) and (max-width: 1024px) and (orientation: landscape) {
    font-size: 1.5rem;
    
    svg {
      font-size: 1.7rem;
    }
  }

  @media (max-width: 768px) {
    font-size: 1.5rem;
    
    svg {
      font-size: 1.7rem;
    }
  }
`;

const HeroText = styled.div`
  h1 {
    font-size: 3.5rem;
    font-weight: 700;
    margin-bottom: 1.5rem;
    line-height: 1.1;
    opacity: 0;
    animation: ${fadeInUp} 0.8s ease-out 0.4s forwards;
    color: ${props => props.theme.colors.button};

    /* iPad Portrait */
    @media (min-width: 768px) and (max-width: 1024px) and (orientation: portrait) {
      font-size: 3rem;
    }

    /* iPad Landscape */
    @media (min-width: 768px) and (max-width: 1024px) and (orientation: landscape) {
      font-size: 2.8rem;
    }

    /* Small laptops and 125% zoom */
    @media (min-width: 1024px) and (max-width: 1366px) {
      font-size: 3.2rem;
    }

    /* 13-15 inch laptops */
    @media (min-width: 1366px) and (max-width: 1600px) {
      font-size: 3.3rem;
    }

    @media (max-width: 768px) {
      font-size: 2.5rem;
    }

    /* Extra small screens */
    @media (max-width: 480px) {
      font-size: 2rem;
    }
  }

  p {
    font-size: 1.25rem;
    margin-bottom: 2rem;
    opacity: 0;
    animation: ${fadeInUp} 0.8s ease-out 0.6s forwards;
    line-height: 1.6;

    /* iPad Portrait */
    @media (min-width: 768px) and (max-width: 1024px) and (orientation: portrait) {
      font-size: 1.2rem;
    }

    /* iPad Landscape */
    @media (min-width: 768px) and (max-width: 1024px) and (orientation: landscape) {
      font-size: 1.15rem;
    }

    /* Small laptops and 125% zoom */
    @media (min-width: 1024px) and (max-width: 1366px) {
      font-size: 1.2rem;
    }

    /* 13-15 inch laptops */
    @media (min-width: 1366px) and (max-width: 1600px) {
      font-size: 1.25rem;
    }

    @media (max-width: 768px) {
      font-size: 1.1rem;
    }

    /* Extra small screens */
    @media (max-width: 480px) {
      font-size: 1rem;
      margin-bottom: 1.5rem;
    }

    /* Very small screens */
    @media (max-width: 360px) {
      font-size: 0.9rem;
      margin-bottom: 1.25rem;
    }
  }
`;

const SearchContainer = styled.div`
  position: relative;
  max-width: 800px;
  margin-bottom: 2rem;
  opacity: 0;
  animation: ${fadeInUp} 0.8s ease-out 0.8s forwards;
  overflow: visible;
  isolation: isolate;
`;

const SearchWrapper = styled.div`
  background: white;
  border-radius: 50px;
  padding: 0.75rem;
  display: flex;
  align-items: center;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.15);
  gap: 0.5rem;
  min-height: 60px;

  /* iPad Portrait */
  @media (min-width: 768px) and (max-width: 1024px) and (orientation: portrait) {
    gap: 0.75rem;
    padding: 1rem;
    border-radius: 30px;
    min-height: 65px;
  }

  /* iPad Landscape */
  @media (min-width: 768px) and (max-width: 1024px) and (orientation: landscape) {
    gap: 0.5rem;
    padding: 0.875rem;
    border-radius: 25px;
    min-height: 60px;
  }

  /* Small laptops and 125% zoom */
  @media (min-width: 1024px) and (max-width: 1366px) {
    gap: 0.75rem;
    padding: 0.875rem;
    border-radius: 30px;
    min-height: 62px;
  }

  /* 13-15 inch laptops */
  @media (min-width: 1366px) and (max-width: 1600px) {
    gap: 0.75rem;
    padding: 0.875rem;
    border-radius: 30px;
    min-height: 60px;
  }

  @media (max-width: 768px) {
    flex-direction: column;
    gap: 0.75rem;
    padding: 1rem;
    border-radius: 25px;
    min-height: auto;
  }

  /* Extra small screens */
  @media (max-width: 480px) {
    padding: 0.75rem;
    border-radius: 20px;
  }
`;

const SearchInput = styled.input`
  flex: 1;
  padding: 1rem 1rem 1rem 1rem;
  border: none;
  border-radius: 25px;
  font-size: 0.95rem;
  background: transparent;
  color: ${props => props.theme.colors.primary};
  font-weight: 500;

  &::placeholder {
    color: ${props => props.theme.colors.primary};
    opacity: 0.8;
  }

  &:focus {
    outline: none;
  }

  /* iPad Portrait */
  @media (min-width: 768px) and (max-width: 1024px) and (orientation: portrait) {
    font-size: 1rem;
    padding: 0.875rem 1rem;
    min-height: 50px;
  }

  /* iPad Landscape */
  @media (min-width: 768px) and (max-width: 1024px) and (orientation: landscape) {
    font-size: 0.95rem;
    padding: 0.75rem 1rem;
    min-height: 45px;
  }

  @media (max-width: 768px) {
    font-size: 1rem;
    padding: 0.75rem 1rem;
    width: 100%;
    min-height: 44px;
  }
`;

const SearchIcon = styled.div`
  position: absolute;
  left: 1.5rem;
  top: 50%;
  transform: translateY(-50%);
  color: ${props => props.theme.colors.primary};
  font-size: 1.25rem;
  z-index: 2;
  display: none;
`;

const FilterDivider = styled.div`
  width: 1px;
  height: 30px;
  background: #e0e0e0;
  margin: 0 0.5rem;

  /* iPad Portrait */
  @media (min-width: 768px) and (max-width: 1024px) and (orientation: portrait) {
    height: 35px;
    margin: 0 0.75rem;
  }

  /* iPad Landscape */
  @media (min-width: 768px) and (max-width: 1024px) and (orientation: landscape) {
    height: 32px;
    margin: 0 0.6rem;
  }

  @media (max-width: 768px) {
    display: none;
  }
`;

const FilterOption = styled.div`
  padding: 0.75rem 1rem;
  background: white;
  color: ${props => props.theme.colors.primary};
  font-weight: 500;
  font-size: 0.9rem;
  cursor: pointer;
  border-radius: 25px;
  transition: all 0.3s ease;
  position: relative;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  border: 1px solid #e0e0e0;
  min-width: 120px;
  max-width: 180px;
  overflow: hidden;

  &:hover {
    background: #f8f9fa;
    border-color: ${props => props.theme.colors.primary};
  }

  /* iPad Portrait */
  @media (min-width: 768px) and (max-width: 1024px) and (orientation: portrait) {
    font-size: 0.9rem;
    padding: 0.75rem 1rem;
    min-width: 140px;
    max-width: 160px;
    border-radius: 20px;
  }

  /* iPad Landscape */
  @media (min-width: 768px) and (max-width: 1024px) and (orientation: landscape) {
    font-size: 0.85rem;
    padding: 0.7rem 0.9rem;
    min-width: 130px;
    max-width: 150px;
    border-radius: 18px;
  }

  @media (max-width: 768px) {
    font-size: 0.9rem;
    padding: 0.75rem 1rem;
    min-width: 100%;
    max-width: 100%;
    width: 100%;
    justify-content: space-between;
    border-radius: 15px;
  }
`;

const FilterText = styled.span`
  flex: 1;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  min-width: 0;
`;

const DropdownIcon = styled(FiChevronDown)`
  color: ${props => props.theme.colors.primary};
  font-size: 0.9rem;
  transition: transform 0.3s ease;
  transform: ${props => props.isOpen ? 'rotate(180deg)' : 'rotate(0deg)'};
  flex-shrink: 0;

  @media (max-width: 768px) {
    font-size: 0.8rem;
  }
`;

const DropdownMenu = styled.div`
  position: absolute;
  top: 100%;
  left: 0;
  right: 0;
  background: white;
  border-radius: 12px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.15);
  z-index: 9999999;
  max-height: 300px;
  overflow-y: auto;
  margin-top: 0.5rem;
  opacity: ${props => props.isOpen ? '1' : '0'};
  visibility: ${props => props.isOpen ? 'visible' : 'hidden'};
  transform: ${props => props.isOpen ? 'translateY(0)' : 'translateY(-10px)'};
  transition: all 0.3s ease;
  min-width: 200px;
  border: 1px solid #e0e0e0;

  @media (max-width: 768px) {
    width: 100%;
    min-width: 100%;
    max-height: 250px;
    border-radius: 8px;
    margin-top: 0.25rem;
  }
`;

const DropdownItem = styled.div`
  padding: 0.75rem 1rem;
  cursor: pointer;
  transition: background 0.2s ease;
  border-bottom: 1px solid #f0f0f0;
  color: ${props => props.theme.colors.primary};
  font-weight: 500;
  font-size: 0.9rem;
  white-space: normal;
  overflow: visible;
  line-height: 1.4;
  word-wrap: break-word;
  min-height: 44px;
  display: flex;
  align-items: center;
  user-select: none;
  position: relative;

  &:last-child {
    border-bottom: none;
  }

  &:hover {
    background: rgba(1, 103, 95, 0.1);
  }

  &:focus {
    outline: 2px solid ${props => props.theme.colors.primary};
    outline-offset: -2px;
  }

  &[aria-selected="true"] {
    background: rgba(1, 103, 95, 0.15);
    font-weight: 600;
    
    &::after {
      content: '✓';
      position: absolute;
      right: 1rem;
      color: ${props => props.theme.colors.primary};
      font-weight: bold;
    }
  }

  @media (max-width: 768px) {
    font-size: 0.9rem;
    padding: 0.75rem 1rem;
    min-height: 44px;
    display: flex;
    align-items: center;
    line-height: 1.3;
  }
`;

const SearchButton = styled.button`
  background: ${props => props.theme.colors.secondary};
  color: white;
  border: none;
  padding: 0.75rem 1.5rem;
  border-radius: 25px;
  font-weight: 600;
  font-size: 0.9rem;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  cursor: pointer;
  transition: all 0.3s ease;
  white-space: nowrap;
  display: flex;
  align-items: center;
  gap: 0.5rem;

  &:hover {
    background: ${props => props.theme.colors.secondaryDark};
    transform: translateY(-1px);
  }

  /* iPad Portrait */
  @media (min-width: 768px) and (max-width: 1024px) and (orientation: portrait) {
    font-size: 0.9rem;
    padding: 0.75rem 1.5rem;
    min-height: 50px;
    border-radius: 20px;
  }

  /* iPad Landscape */
  @media (min-width: 768px) and (max-width: 1024px) and (orientation: landscape) {
    font-size: 0.85rem;
    padding: 0.7rem 1.3rem;
    min-height: 45px;
    border-radius: 18px;
  }

  @media (max-width: 768px) {
    font-size: 0.9rem;
    padding: 0.75rem 1.5rem;
    width: 100%;
    justify-content: center;
    min-height: 44px;
    border-radius: 15px;
  }
`;

const SearchSuggestions = styled.div`
  position: absolute;
  top: 100%;
  left: 0;
  right: 0;
  background: white;
  border-radius: 8px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.15);
  z-index: 9999999;
  max-height: 300px;
  overflow-y: auto;
  display: ${props => props.show ? 'block' : 'none'};
  isolation: isolate;

  @media (max-width: 768px) {
    max-height: 200px;
    border-radius: 8px;
    margin-top: 0.25rem;
  }
`;

const SuggestionItem = styled.div`
  padding: 0.75rem 1rem;
  cursor: pointer;
  border-bottom: 1px solid #f0f0f0;
  color: ${props => props.theme.colors.text};
  line-height: 1.4;
  word-wrap: break-word;
  min-height: 44px;
  display: flex;
  flex-direction: column;
  justify-content: center;

  &:hover {
    background: #f8f9fa;
  }

  &:last-child {
    border-bottom: none;
  }

  @media (max-width: 768px) {
    padding: 0.75rem 1rem;
    min-height: 44px;
    display: flex;
    flex-direction: column;
    justify-content: center;
    line-height: 1.3;
  }
`;

const SuggestionCategory = styled.div`
  font-weight: 600;
  color: ${props => props.theme.colors.primary};
  font-size: 0.9rem;
`;

const SuggestionLevel = styled.div`
  font-size: 0.85rem;
  color: ${props => props.theme.colors.textLight};
  margin-top: 0.25rem;
`;

const HeroImages = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 1.5rem;
  max-width: 600px;
  margin: 0 auto;
  opacity: 0;
  animation: ${scaleIn} 0.8s ease-out 1s forwards;

  /* 150% zoom and small laptops */
  @media (min-width: 1024px) and (max-width: 1366px) {
    gap: 1.25rem;
    max-width: 550px;
  }

  /* 175% zoom and medium laptops */
  @media (min-width: 1366px) and (max-width: 1600px) {
    gap: 1.75rem;
    max-width: 580px;
  }

  /* 200% zoom and large screens */
  @media (min-width: 1600px) and (max-width: 1920px) {
    gap: 2rem;
    max-width: 600px;
  }

  /* iPad Portrait */
  @media (min-width: 768px) and (max-width: 1024px) and (orientation: portrait) {
    display: none;
  }

  /* iPad Landscape */
  @media (min-width: 768px) and (max-width: 1024px) and (orientation: landscape) {
    display: none;
  }

  @media (max-width: 768px) {
    display: none;
  }
`;

const ImageCard = styled.div`
  aspect-ratio: 1;
  width: 100%;
  height: 100%;
  overflow: hidden;
  position: relative;
  background: #f8f9fa;
  min-height: 200px;
  min-width: 200px;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: transform 0.3s ease;
  /* Ensure shapes remain as defined; prevent any later style from rounding */
  will-change: transform;

  &:hover {
    transform: scale(1.05);
  }

  /* Top-left */
  &:nth-child(1) {
    border-radius: 120px 0 0 0 !important;
    grid-column: 1 / 2;
    grid-row: 1 / 2;
  }
  /* Top-right */
  &:nth-child(2) {
    border-radius: 32px !important;
    grid-column: 2 / 3;
    grid-row: 1 / 2;
  }
  /* Bottom-left */
  &:nth-child(3) {
    border-radius: 32px !important;
    grid-column: 1 / 2;
    grid-row: 2 / 3;
  }
  /* Bottom-right */
  &:nth-child(4) {
    border-radius: 0 0 120px 0 !important;
    grid-column: 2 / 3;
    grid-row: 2 / 3;
  }
`;

const ProfessionalImage = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
  position: relative;
  z-index: 1;
  /* Inherit the parent card's border radius to prevent late rounding glitches */
  border-radius: inherit !important;
`;



const HeroSection = () => {
  const [searchQuery, setSearchQuery] = useState('');
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [suggestions, setSuggestions] = useState([]);
  const [categories, setCategories] = useState([]);
  const [levels, setLevels] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState('All Categories');
  const [selectedLevel, setSelectedLevel] = useState('All Levels');
  const [showCategoryDropdown, setShowCategoryDropdown] = useState(false);
  const [showLevelDropdown, setShowLevelDropdown] = useState(false);

  // Fetch categories and levels from database
  useEffect(() => {
    const fetchSearchOptions = async () => {
      try {
        const data = await getSearchOptions();
        setCategories(data.categories);
        setLevels(data.levels);
      } catch (error) {
        console.error('Error fetching search options:', error);
      }
    };

    fetchSearchOptions();
  }, []);

  // Close dropdowns when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      const target = event.target;
      const isDropdownClick = target.closest('.filter-dropdown') || 
                             target.closest('.dropdown-menu') ||
                             target.closest('.dropdown-item');
      
      if (!isDropdownClick) {
        setShowCategoryDropdown(false);
        setShowLevelDropdown(false);
      }
    };

    const handleEscapeKey = (event) => {
      if (event.key === 'Escape') {
        setShowCategoryDropdown(false);
        setShowLevelDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('keydown', handleEscapeKey);
    
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleEscapeKey);
    };
  }, []);

  const handleSearchChange = (e) => {
    const query = e.target.value;
    setSearchQuery(query);

    if (query.length > 1) {
      // Create suggestions based on categories and levels
      const suggestions = [];
      
      // Add category matches
      categories.forEach(category => {
        if (category.name.toLowerCase().includes(query.toLowerCase())) {
          suggestions.push({
            type: 'category',
            name: category.name,
            slug: category.slug
          });
        }
      });
      
      // Add level matches
      levels.forEach(level => {
        if (level.toLowerCase().includes(query.toLowerCase())) {
          suggestions.push({
            type: 'level',
            name: level
          });
        }
      });
      
      setSuggestions(suggestions.slice(0, 8));
      setShowSuggestions(true);
    } else {
      setShowSuggestions(false);
    }
  };

  const handleSuggestionClick = (suggestion) => {
    if (suggestion.type === 'category') {
      setSearchQuery(suggestion.name);
      setSelectedCategory(suggestion.name);
    } else if (suggestion.type === 'level') {
      setSearchQuery(suggestion.name);
      setSelectedLevel(suggestion.name);
    }
    setShowSuggestions(false);
  };

  const handleDropdownKeyDown = (e, dropdownType) => {
    if (e.key === 'Escape') {
      if (dropdownType === 'category') {
        setShowCategoryDropdown(false);
      } else if (dropdownType === 'level') {
        setShowLevelDropdown(false);
      }
    }
  };



  const handleSearch = (e) => {
    e.preventDefault();
    const params = new URLSearchParams();
    
    if (searchQuery.trim()) {
      params.append('search', searchQuery.trim());
    }
    
    if (selectedCategory !== 'All Categories') {
      const category = categories.find(cat => cat.name === selectedCategory);
      if (category) {
        params.append('category', category.slug);
      }
    }
    
    if (selectedLevel !== 'All Levels') {
      params.append('level', selectedLevel);
    }
    
    const queryString = params.toString();
    window.location.href = `/search${queryString ? `?${queryString}` : ''}`;
  };

  const heroImages = [
    {
      image: '/hero-main-qualification.png',
      fallback: <FiUsers />
    },
    {
      image: '/hero-construction-workers.png', // Construction workers
      fallback: <FiHardDrive />
    },
    {
      image: '/hero-education-training.png',
      fallback: <FiScissors />
    },
    {
      image: '/hero-professional-development.png',
      fallback: <FiMonitor />
    }
  ];

  return (
    <HeroContainer>
      <HeroContent>
        <HeroText>
          <CertifiedBadge>
            <FiStar />
            Get Your Skills Certified
          </CertifiedBadge>
          
          <h1>Take Your Career To The Next Level</h1>
          <p>
            Get your skills counted with RPL Support. We help you gain nationally recognized 
            qualifications through Recognition of Prior Learning, advancing your career with 
            the experience you already have.
          </p>
          
          <SearchContainer>
            <form onSubmit={handleSearch}>
              <SearchWrapper>
                <div style={{ position: 'relative', flex: 1 }}>
                  <SearchInput
                    type="text"
                    placeholder="Search your Course"
                    value={searchQuery}
                    onChange={handleSearchChange}
                    onFocus={() => searchQuery.length > 1 && setShowSuggestions(true)}
                    onBlur={() => setTimeout(() => setShowSuggestions(false), 200)}
                  />
                </div>
                
                <FilterDivider />
                
                <div className="filter-dropdown" style={{ position: 'relative' }}>
                  <FilterOption
                    onClick={() => setShowCategoryDropdown(!showCategoryDropdown)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        setShowCategoryDropdown(!showCategoryDropdown);
                      }
                    }}
                    tabIndex={0}
                    role="button"
                    aria-expanded={showCategoryDropdown}
                    aria-haspopup="listbox"
                  >
                    <FilterText>{selectedCategory}</FilterText>
                    <DropdownIcon isOpen={showCategoryDropdown} />
                  </FilterOption>
                  
                  <DropdownMenu 
                    isOpen={showCategoryDropdown} 
                    className="dropdown-menu"
                    role="listbox"
                    aria-label="Select category"
                    onKeyDown={(e) => handleDropdownKeyDown(e, 'category')}
                  >
                    <DropdownItem
                      className="dropdown-item"
                      role="option"
                      aria-selected={selectedCategory === 'All Categories'}
                      onClick={() => {
                        setSelectedCategory('All Categories');
                        setShowCategoryDropdown(false);
                      }}
                    >
                      All Categories
                    </DropdownItem>
                    {categories.map((category) => (
                      <DropdownItem
                        key={category._id}
                        className="dropdown-item"
                        role="option"
                        aria-selected={selectedCategory === category.name}
                        onClick={() => {
                          setSelectedCategory(category.name);
                          setShowCategoryDropdown(false);
                        }}
                      >
                        {category.name}
                      </DropdownItem>
                    ))}
                  </DropdownMenu>
                </div>
                
                <FilterDivider />
                
                <div className="filter-dropdown" style={{ position: 'relative' }}>
                  <FilterOption
                    onClick={() => setShowLevelDropdown(!showLevelDropdown)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        setShowLevelDropdown(!showLevelDropdown);
                      }
                    }}
                    tabIndex={0}
                    role="button"
                    aria-expanded={showLevelDropdown}
                    aria-haspopup="listbox"
                  >
                    <FilterText>{selectedLevel}</FilterText>
                    <DropdownIcon isOpen={showLevelDropdown} />
                  </FilterOption>
                  
                  <DropdownMenu 
                    isOpen={showLevelDropdown} 
                    className="dropdown-menu"
                    role="listbox"
                    aria-label="Select level"
                    onKeyDown={(e) => handleDropdownKeyDown(e, 'level')}
                  >
                    <DropdownItem
                      className="dropdown-item"
                      role="option"
                      aria-selected={selectedLevel === 'All Levels'}
                      onClick={() => {
                        setSelectedLevel('All Levels');
                        setShowLevelDropdown(false);
                      }}
                    >
                      All Levels
                    </DropdownItem>
                    {levels.map((level) => (
                      <DropdownItem
                        key={level}
                        className="dropdown-item"
                        role="option"
                        aria-selected={selectedLevel === level}
                        onClick={() => {
                          setSelectedLevel(level);
                          setShowLevelDropdown(false);
                        }}
                      >
                        {level}
                      </DropdownItem>
                    ))}
                  </DropdownMenu>
                </div>
                
                <SearchButton type="submit">
                  <FiSearch />
                </SearchButton>
              </SearchWrapper>
            </form>
            
            <SearchSuggestions show={showSuggestions}>
              {suggestions.map((suggestion, index) => (
                <SuggestionItem
                  key={index}
                  onClick={() => handleSuggestionClick(suggestion)}
                >
                  <SuggestionCategory>
                    {suggestion.type === 'category' ? 'Category' : 'Level'}
                  </SuggestionCategory>
                  <SuggestionLevel>{suggestion.name}</SuggestionLevel>
                </SuggestionItem>
              ))}
            </SearchSuggestions>
          </SearchContainer>
        </HeroText>

                                              <HeroImages>
                      {heroImages.map((image, index) => (
                        <ImageCard key={index}>
                          <ProfessionalImage 
                            src={image.image} 
                            alt="Professional"
                            onError={(e) => {
                              e.target.style.display = 'none';
                              e.target.nextSibling.style.display = 'flex';
                            }}
                          />
                        </ImageCard>
                      ))}
                    </HeroImages>
      </HeroContent>
    </HeroContainer>
  );
};

export default HeroSection; 